/* eslint-disable quotes */
'use client';

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Check,
  ArrowRight,
  Wrench,
  Droplets,
  Zap,
  Hammer,
  Paintbrush,
  Sparkles,
  ClipboardList,
  ShieldCheck,
  Wind,
  Settings2,
  FileText,
  Phone,
  Mail,
  MapPin,
  CheckCircle,
  Search,
} from 'lucide-react';
import ContactFormModal from '@/app/components/contact/contact-form-modal-pixxi';

// ─── HomeCare colour constants ────────────────────────────────────────────────
// Primary:      #0F766E  (teal-700)
// Dark:         #134E4A  (teal-900)
// Light BG:     #F0FDFA  (teal-50)
// Light accent: #CCFBF1  (teal-100)
// Hover:        #115E59  (teal-800)
// ─────────────────────────────────────────────────────────────────────────────

const acCategories = [
  {
    title: 'Inspection & Diagnosis',
    icon: <Search className='h-5 w-5' />,
    items: [
      'Emergency AC breakdown support',
      'AC inspection and fault diagnosis',
      'Water leakage inspection',
      'Noise and vibration inspection',
    ],
  },
  {
    title: 'Cleaning & Preventive Care',
    icon: <Wind className='h-5 w-5' />,
    items: [
      'Preventive AC maintenance',
      'Filter and coil cleaning',
      'Blower and fan cleaning',
      'Drain line and drain pan cleaning',
    ],
  },
  {
    title: 'Repair & Replacement',
    icon: <Wrench className='h-5 w-5' />,
    items: [
      'Refrigerant leak testing',
      'Refrigerant charging',
      'Electrical component inspection',
      'Capacitor and contactor replacement',
      'Fan motor replacement',
      'Sensor and thermostat inspection',
      'PCB inspection and replacement',
    ],
  },
  {
    title: 'Testing & Handover',
    icon: <CheckCircle className='h-5 w-5' />,
    items: [
      'Cooling performance testing',
      'Final cleaning and customer handover',
    ],
  },
];

const additionalServices = [
  {
    title: 'Plumbing Services',
    description:
      'Inspection and repair of water leaks, drainage problems, blocked pipes, taps, toilets and other common plumbing issues.',
    icon: <Droplets className='h-7 w-7' />,
    active: false,
  },
  {
    title: 'Electrical Services',
    description:
      'Support for electrical faults, switches, sockets, lighting fixtures, circuit issues and minor electrical maintenance.',
    icon: <Zap className='h-7 w-7' />,
    active: false,
  },
  {
    title: 'Handyman Services',
    description:
      'Professional assistance with installations, furniture assembly, wall mounting, minor repairs and general property maintenance.',
    icon: <Hammer className='h-7 w-7' />,
    active: false,
  },
  {
    title: 'Painting & Minor Repairs',
    description:
      'Interior touch-ups, wall repair, repainting and preparation of properties before leasing or handover.',
    icon: <Paintbrush className='h-7 w-7' />,
    active: false,
  },
  {
    title: 'Deep Cleaning',
    description:
      'Detailed cleaning solutions for occupied, vacant, move-in and move-out properties.',
    icon: <Sparkles className='h-7 w-7' />,
    active: false,
  },
  {
    title: 'Property Inspection',
    description:
      'Property condition inspections supported by photographs and documented maintenance recommendations.',
    icon: <ClipboardList className='h-7 w-7' />,
    active: false,
  },
];

const processSteps = [
  {
    step: '01',
    title: 'Submit Your Request',
    description: 'Provide the property location and details of the maintenance issue.',
    icon: <FileText className='h-6 w-6' />,
  },
  {
    step: '02',
    title: 'Inspection & Diagnosis',
    description: 'Our team inspects the affected system and identifies the root cause.',
    icon: <Settings2 className='h-6 w-6' />,
  },
  {
    step: '03',
    title: 'Quotation & Approval',
    description:
      'We explain the recommended solution and obtain approval before major repair work.',
    icon: <ClipboardList className='h-6 w-6' />,
  },
  {
    step: '04',
    title: 'Professional Maintenance',
    description:
      'The approved work is completed safely while protecting the surrounding property.',
    icon: <Wrench className='h-6 w-6' />,
  },
  {
    step: '05',
    title: 'Testing & Handover',
    description:
      'The system is tested, the area is cleaned and the completed work is explained to the customer.',
    icon: <CheckCircle className='h-6 w-6' />,
  },
];

const benefits = [
  'Professional and supervised maintenance team',
  'Safety-focused working procedures',
  'Protection of furniture, flooring and walls',
  'Transparent diagnosis and repair recommendations',
  'Before-and-after photographs',
  'Approved replacement parts',
  'Complete system testing',
  'Clean worksite handover',
  'Maintenance documentation',
  'Support for landlords, tenants and investors',
];

const plans = [
  {
    name: 'Essential Care',
    subtitle: 'Flexible support for individual repair and maintenance requests.',
    features: [
      'Inspection and diagnosis',
      'Maintenance recommendations',
      'Pay-per-visit service',
      'Service completion report',
    ],
    cta: 'Request Service',
  },
  {
    name: 'Complete Care',
    subtitle: 'Preventive maintenance support for occupied residential properties.',
    features: [
      'Scheduled maintenance visits',
      'Priority booking',
      'AC performance inspection',
      'Maintenance reminders',
      'Preferential labour rates',
    ],
    cta: 'Request a Quote',
    featured: true,
  },
  {
    name: 'Landlord Care',
    subtitle: 'Property maintenance support designed for landlords and investors.',
    features: [
      'Tenant maintenance coordination',
      'Property condition inspections',
      'Before-and-after photographs',
      'Quotation approval workflow',
      'Maintenance history',
      'Move-in and move-out support',
      'Multiple-property management',
    ],
    cta: 'Discuss Your Properties',
  },
];

function PropertyCareClient() {
  const [isMounted, setIsMounted] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: 'easeOut' },
    },
  };

  const staggerContainer = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.2 } },
  };

  if (!isMounted) return null;

  return (
    <main className='bg-white dark:bg-gray-950 text-gray-900 dark:text-white overflow-hidden'>

      {/* ── 1. HERO ─────────────────────────────────────────────────────────── */}
      <section aria-label='Hero' className='relative min-h-[80vh] flex items-center justify-center overflow-hidden pt-20'>
        <div className='absolute inset-0 bg-gradient-to-br from-teal-50 to-white dark:from-gray-900 dark:to-gray-800 z-0'>
          <motion.div
            className='absolute inset-0 opacity-30 dark:opacity-40'
            animate={{
              background: [
                'radial-gradient(circle at 20% 30%, rgba(15,118,110,0.15) 0%, rgba(20,184,166,0.05) 50%, rgba(0,0,0,0) 100%)',
                'radial-gradient(circle at 80% 70%, rgba(20,184,166,0.15) 0%, rgba(15,118,110,0.05) 50%, rgba(0,0,0,0) 100%)',
                'radial-gradient(circle at 20% 30%, rgba(15,118,110,0.15) 0%, rgba(20,184,166,0.05) 50%, rgba(0,0,0,0) 100%)',
              ],
            }}
            transition={{ duration: 15, repeat: Number.POSITIVE_INFINITY, ease: 'easeInOut' }}
          />
        </div>

        <div className='absolute inset-0 overflow-hidden z-0'>
          <motion.div
            className='absolute -top-20 -left-20 w-96 h-96 rounded-full bg-teal-700/5 dark:bg-teal-500/10'
            animate={{ scale: [1, 1.1, 1], rotate: [0, 10, 0] }}
            transition={{ repeat: Number.POSITIVE_INFINITY, duration: 20, ease: 'easeInOut' }}
          />
          <motion.div
            className='absolute -bottom-40 -right-40 w-[30rem] h-[30rem] rounded-full bg-teal-500/5 dark:bg-teal-400/10'
            animate={{ scale: [1, 1.2, 1], rotate: [0, -10, 0] }}
            transition={{ repeat: Number.POSITIVE_INFINITY, duration: 25, ease: 'easeInOut' }}
          />
        </div>

        <div className='container mx-auto px-4 z-10 py-20'>
          <div className='max-w-5xl mx-auto text-center'>
            <motion.div
              className='inline-block px-6 py-2 bg-teal-700/10 rounded-full text-teal-700 dark:text-teal-400 font-medium mb-6 tracking-widest text-sm uppercase'
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              X SPACE HOMECARE
            </motion.div>

            <motion.h1
              className='text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-teal-700 to-teal-500 bg-clip-text text-transparent'
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              Property Maintenance Dubai{' '}
              <span className='block'>— Professionally Cared For.</span>
            </motion.h1>

            <motion.p
              className='text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto mb-4'
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              Reliable maintenance and home support for landlords, tenants, homeowners and
              property investors across Dubai.
            </motion.p>

            <motion.p
              className='text-lg text-gray-500 dark:text-gray-500 max-w-3xl mx-auto mb-10'
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.75 }}
            >
              From urgent air-conditioning repairs to scheduled preventive maintenance,
              X Space HomeCare helps protect your property, reduce unexpected breakdowns
              and maintain a safe and comfortable living environment.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.9 }}
              className='flex flex-col sm:flex-row justify-center gap-4'
            >
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsContactModalOpen(true)}
                className='px-8 py-4 bg-gradient-to-r from-teal-700 to-teal-600 text-white rounded-full font-medium flex items-center justify-center group shadow-lg hover:from-teal-800 hover:to-teal-700 transition-all'
                aria-label='Request a HomeCare service'
              >
                Request a Service
                <ArrowRight className='ml-2 h-5 w-5 transition-transform group-hover:translate-x-1' />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsContactModalOpen(true)}
                className='px-8 py-4 bg-white dark:bg-gray-800 text-teal-700 dark:text-teal-400 border-2 border-teal-700 dark:border-teal-500 rounded-full font-medium hover:bg-teal-700 hover:text-white dark:hover:bg-teal-700 dark:hover:text-white transition-colors shadow-lg'
                aria-label='Get an annual maintenance quote'
              >
                Get an Annual Maintenance Quote
              </motion.button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── 2. AC REPAIR & MAINTENANCE ──────────────────────────────────────── */}
      <section aria-labelledby='ac-maintenance-heading' className='py-20 px-4 bg-gray-50 dark:bg-gray-900'>
        <div className='container mx-auto'>
          <motion.div
            initial='hidden'
            whileInView='visible'
            viewport={{ once: true }}
            variants={staggerContainer}
            className='text-center mb-16'
          >
            <motion.div
              variants={fadeInUp}
              className='inline-block px-6 py-2 bg-teal-700/10 rounded-full text-teal-700 dark:text-teal-400 font-medium mb-4'
            >
              Core Service — Active Now
            </motion.div>
            <motion.h2
              id='ac-maintenance-heading'
              variants={fadeInUp}
              className='text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4'
            >
              AC Repair &amp; Maintenance
            </motion.h2>
            <motion.div
              variants={fadeInUp}
              className='w-24 h-1 bg-gradient-to-r from-teal-700 to-teal-500 mx-auto mb-6'
            />
            <motion.p
              variants={fadeInUp}
              className='text-gray-700 dark:text-gray-300 text-lg max-w-3xl mx-auto'
            >
              Professional air-conditioning inspection, diagnosis, servicing and repair for
              residential and commercial properties.
            </motion.p>
          </motion.div>

          <div className='grid grid-cols-1 md:grid-cols-2 gap-8 mb-12'>
            {acCategories.map((cat, idx) => (
              <motion.div
                key={cat.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                viewport={{ once: true }}
                className='bg-white dark:bg-gray-800 p-8 rounded-3xl shadow-xl border border-gray-100 dark:border-gray-700 hover:shadow-2xl transition-all duration-300'
              >
                <div className='flex items-center gap-3 mb-5'>
                  <div className='w-10 h-10 rounded-full bg-teal-700/10 flex items-center justify-center text-teal-700 dark:text-teal-400'>
                    {cat.icon}
                  </div>
                  <h3 className='text-lg font-bold text-gray-900 dark:text-white'>
                    {cat.title}
                  </h3>
                </div>
                <ul className='space-y-3'>
                  {cat.items.map((item) => (
                    <li key={item} className='flex items-start gap-3'>
                      <div aria-hidden='true' className='w-5 h-5 rounded-full bg-teal-700/10 flex items-center justify-center mt-0.5 shrink-0'>
                        <Check className='w-3 h-3 text-teal-700 dark:text-teal-400' />
                      </div>
                      <span className='text-gray-700 dark:text-gray-300 text-sm'>{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className='bg-teal-50 dark:bg-teal-900/20 border border-teal-100 dark:border-teal-800 rounded-2xl px-8 py-6 text-center max-w-4xl mx-auto'
          >
            <div className='flex items-center justify-center gap-2 mb-2'>
              <Wind className='h-5 w-5 text-teal-700 dark:text-teal-400' />
              <span className='font-semibold text-teal-800 dark:text-teal-300'>
                Supported Systems
              </span>
            </div>
            <p className='text-gray-700 dark:text-gray-300'>
              We service split units, ducted split systems, package units, VRF and VRV systems,
              FCUs, AHUs, and ventilation systems.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── 3. ADDITIONAL HOMECARE SERVICES ─────────────────────────────────── */}
      <section aria-labelledby='additional-services-heading' className='py-20 px-4'>
        <div className='container mx-auto'>
          <motion.div
            initial='hidden'
            whileInView='visible'
            viewport={{ once: true }}
            variants={staggerContainer}
            className='text-center mb-16'
          >
            <motion.h2
              id='additional-services-heading'
              variants={fadeInUp}
              className='text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4'
            >
              Additional HomeCare Services
            </motion.h2>
            <motion.div
              variants={fadeInUp}
              className='w-24 h-1 bg-gradient-to-r from-teal-700 to-teal-500 mx-auto mb-6'
            />
            <motion.p
              variants={fadeInUp}
              className='text-gray-700 dark:text-gray-300 text-lg max-w-3xl mx-auto'
            >
              We are expanding our range of home support services. AC Repair &amp; Maintenance
              is available now — additional services are launching soon.
            </motion.p>
          </motion.div>

          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'>
            {additionalServices.map((svc, idx) => (
              <motion.div
                key={svc.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                viewport={{ once: true }}
                className='relative bg-white dark:bg-gray-800 p-8 rounded-3xl shadow-xl border border-gray-100 dark:border-gray-700 hover:shadow-2xl transition-all duration-300 group'
                aria-label={`${svc.title} – Coming Soon`}
              >
                <div className='absolute top-4 right-4'>
                  <span className='px-3 py-1 bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-400 text-xs font-semibold rounded-full'>
                    Coming Soon
                  </span>
                </div>
                <div className='w-14 h-14 rounded-full bg-teal-700/10 flex items-center justify-center mb-5 text-teal-700 dark:text-teal-400 group-hover:scale-110 transition-transform'>
                  {svc.icon}
                </div>
                <h3 className='text-xl font-bold text-gray-900 dark:text-white mb-3'>
                  {svc.title}
                </h3>
                <p className='text-gray-600 dark:text-gray-400 text-sm leading-relaxed'>
                  {svc.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 4. HOW IT WORKS ─────────────────────────────────────────────────── */}
      <section aria-labelledby='how-it-works-heading' className='py-20 px-4 bg-gray-50 dark:bg-gray-900'>
        <div className='container mx-auto'>
          <motion.div
            initial='hidden'
            whileInView='visible'
            viewport={{ once: true }}
            variants={staggerContainer}
            className='text-center mb-16'
          >
            <motion.h2
              id='how-it-works-heading'
              variants={fadeInUp}
              className='text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4'
            >
              How It Works
            </motion.h2>
            <motion.div
              variants={fadeInUp}
              className='w-24 h-1 bg-gradient-to-r from-teal-700 to-teal-500 mx-auto mb-6'
            />
            <motion.p
              variants={fadeInUp}
              className='text-gray-700 dark:text-gray-300 text-lg max-w-3xl mx-auto'
            >
              A simple, transparent process from first contact to completed handover.
            </motion.p>
          </motion.div>

          <ol className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 list-none'>
            {processSteps.map((step, idx) => (
              <motion.li
                key={step.step}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                viewport={{ once: true }}
                className='relative bg-white dark:bg-gray-800 p-6 rounded-3xl shadow-xl border border-gray-100 dark:border-gray-700 hover:shadow-2xl transition-all duration-300'
              >
                <div aria-hidden='true' className='absolute -top-4 -left-4 w-10 h-10 bg-gradient-to-r from-teal-700 to-teal-600 rounded-full flex items-center justify-center text-white font-bold text-sm'>
                  {step.step}
                </div>
                <div aria-hidden='true' className='w-10 h-10 rounded-full bg-teal-700/10 flex items-center justify-center mb-4 text-teal-700 dark:text-teal-400'>
                  {step.icon}
                </div>
                <h3 className='text-base font-bold text-gray-900 dark:text-white mb-2'>
                  {step.title}
                </h3>
                <p className='text-gray-600 dark:text-gray-400 text-sm leading-relaxed'>
                  {step.description}
                </p>
              </motion.li>
            ))}
          </ol>
        </div>
      </section>

      {/* ── 5. WHY CHOOSE X SPACE HOMECARE ──────────────────────────────────── */}
      <section aria-labelledby='why-choose-heading' className='py-20 px-4'>
        <div className='container mx-auto'>
          <div className='flex flex-col lg:flex-row items-center gap-12 lg:gap-20'>
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className='w-full lg:w-1/2'
            >
              <div className='inline-block px-6 py-2 bg-teal-700/10 rounded-full text-teal-700 dark:text-teal-400 font-medium mb-4'>
                Why Choose HomeCare
              </div>
              <h2 id='why-choose-heading' className='text-4xl font-bold text-gray-900 dark:text-white mb-6'>
                Professional care that protects your property and your investment.
              </h2>
              <p className='text-lg text-gray-600 dark:text-gray-400 mb-8'>
                Every service is carried out by a trained, supervised team with a focus on
                safety, transparency and quality workmanship.
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsContactModalOpen(true)}
                className='px-8 py-3 bg-gradient-to-r from-teal-700 to-teal-600 text-white rounded-full font-medium flex items-center group hover:from-teal-800 hover:to-teal-700 transition-all'
                aria-label='Request HomeCare service'
              >
                Request HomeCare Service
                <ArrowRight className='ml-2 h-5 w-5 transition-transform group-hover:translate-x-1' />
              </motion.button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className='w-full lg:w-1/2'
            >
              <div className='bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-xl border border-gray-100 dark:border-gray-700'>
                <ul className='grid grid-cols-1 sm:grid-cols-2 gap-4 list-none'>
                  {benefits.map((benefit) => (
                    <li key={benefit} className='flex items-start gap-3'>
                      <div aria-hidden='true' className='w-6 h-6 rounded-full bg-gradient-to-r from-teal-700 to-teal-600 flex items-center justify-center shrink-0 mt-0.5'>
                        <Check className='w-3 h-3 text-white' />
                      </div>
                      <span className='text-gray-700 dark:text-gray-300 text-sm'>
                        {benefit}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── 6. HOMECARE PLANS ───────────────────────────────────────────────── */}
      <section aria-labelledby='homecare-plans-heading' className='py-20 px-4 bg-gray-50 dark:bg-gray-900'>
        <div className='container mx-auto'>
          <motion.div
            initial='hidden'
            whileInView='visible'
            viewport={{ once: true }}
            variants={staggerContainer}
            className='text-center mb-16'
          >
            <motion.h2
              id='homecare-plans-heading'
              variants={fadeInUp}
              className='text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4'
            >
              HomeCare Plans
            </motion.h2>
            <motion.div
              variants={fadeInUp}
              className='w-24 h-1 bg-gradient-to-r from-teal-700 to-teal-500 mx-auto mb-6'
            />
            <motion.p
              variants={fadeInUp}
              className='text-gray-700 dark:text-gray-300 text-lg max-w-3xl mx-auto'
            >
              Choose the level of support that best fits your property and circumstances.
            </motion.p>
          </motion.div>

          <div className='grid grid-cols-1 md:grid-cols-3 gap-8 mb-8'>
            {plans.map((plan, idx) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                viewport={{ once: true }}
                className={`relative bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-xl border transition-all duration-300 hover:shadow-2xl ${
                  plan.featured
                    ? 'border-teal-700 dark:border-teal-500 ring-2 ring-teal-700/20 dark:ring-teal-500/20'
                    : 'border-gray-100 dark:border-gray-700'
                }`}
              >
                {plan.featured && (
                  <div className='absolute -top-4 left-1/2 -translate-x-1/2'>
                    <span className='px-4 py-1.5 bg-gradient-to-r from-teal-700 to-teal-600 text-white text-xs font-bold rounded-full shadow-lg'>
                      Most Popular
                    </span>
                  </div>
                )}

                <div className='w-12 h-12 rounded-full bg-teal-700/10 flex items-center justify-center mb-5 text-teal-700 dark:text-teal-400'>
                  <ShieldCheck className='h-6 w-6' />
                </div>

                <h3 className='text-2xl font-bold text-gray-900 dark:text-white mb-2'>
                  {plan.name}
                </h3>
                <p className='text-gray-600 dark:text-gray-400 text-sm mb-6'>
                  {plan.subtitle}
                </p>

                <ul className='space-y-3 mb-8'>
                  {plan.features.map((feat) => (
                    <li key={feat} className='flex items-center gap-3'>
                      <div aria-hidden='true' className='w-5 h-5 rounded-full bg-teal-700/10 flex items-center justify-center shrink-0'>
                        <Check className='w-3 h-3 text-teal-700 dark:text-teal-400' />
                      </div>
                      <span className='text-gray-700 dark:text-gray-300 text-sm'>{feat}</span>
                    </li>
                  ))}
                </ul>

                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => setIsContactModalOpen(true)}
                  className={`w-full px-6 py-3 rounded-full font-medium flex items-center justify-center group transition-all ${
                    plan.featured
                      ? 'bg-gradient-to-r from-teal-700 to-teal-600 text-white hover:from-teal-800 hover:to-teal-700'
                      : 'bg-teal-700/10 text-teal-700 dark:text-teal-400 hover:bg-teal-700 hover:text-white dark:hover:bg-teal-700'
                  }`}
                  aria-label={plan.cta}
                >
                  {plan.cta}
                  <ArrowRight className='ml-2 h-4 w-4 transition-transform group-hover:translate-x-1' />
                </motion.button>
              </motion.div>
            ))}
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className='text-center text-sm text-gray-500 dark:text-gray-500 italic'
          >
            Custom plans available based on property type and number of units.
          </motion.p>
        </div>
      </section>

      {/* ── 7. FINAL CTA ────────────────────────────────────────────────────── */}
      <section aria-labelledby='cta-heading' className='py-20 px-4'>
        <div className='container mx-auto'>
          <div className='relative overflow-hidden rounded-3xl bg-gradient-to-r from-teal-700/10 to-teal-500/10 dark:from-teal-700/20 dark:to-teal-500/20 shadow-xl'>
            <div className='absolute top-0 left-0 w-12 h-12 border-t-2 border-l-2 border-teal-700 rounded-tl-3xl z-10' />
            <div className='absolute top-0 right-0 w-12 h-12 border-t-2 border-r-2 border-teal-700 rounded-tr-3xl z-10' />
            <div className='absolute bottom-0 left-0 w-12 h-12 border-b-2 border-l-2 border-teal-700 rounded-bl-3xl z-10' />
            <div className='absolute bottom-0 right-0 w-12 h-12 border-b-2 border-r-2 border-teal-700 rounded-br-3xl z-10' />

            <div className='p-12 relative z-10 text-center'>
              <motion.h2
                id='cta-heading'
                className='text-3xl md:text-4xl font-bold mb-6 text-gray-900 dark:text-white'
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
              >
                Protect Your Property with X Space HomeCare
              </motion.h2>
              <motion.p
                className='text-lg text-gray-700 dark:text-gray-300 mb-10 max-w-3xl mx-auto'
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                viewport={{ once: true }}
              >
                Whether you need an urgent repair, scheduled AC maintenance or long-term
                property care, our team is ready to support you.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                viewport={{ once: true }}
                className='flex flex-col sm:flex-row justify-center gap-4 mb-10'
              >
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setIsContactModalOpen(true)}
                  className='px-8 py-4 bg-gradient-to-r from-teal-700 to-teal-600 text-white rounded-full font-medium hover:from-teal-800 hover:to-teal-700 transition-all shadow-lg'
                  aria-label='Request HomeCare service'
                >
                  Request HomeCare Service
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setIsContactModalOpen(true)}
                  className='px-8 py-4 bg-white dark:bg-gray-800 text-teal-700 dark:text-teal-400 border-2 border-teal-700 dark:border-teal-500 rounded-full font-medium hover:bg-teal-700 hover:text-white dark:hover:bg-teal-700 dark:hover:text-white transition-colors shadow-lg'
                  aria-label='Speak to the maintenance team'
                >
                  Speak to Our Maintenance Team
                </motion.button>
              </motion.div>

              <div className='grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto'>
                <div className='flex items-center justify-center space-x-3'>
                  <Phone className='h-5 w-5 text-teal-700 dark:text-teal-400' />
                  <div className='flex items-center space-x-2'>
                    <a
                      href='tel:04-3965199'
                      className='text-gray-700 dark:text-gray-300 hover:text-teal-700 dark:hover:text-teal-400 transition-colors'
                    >
                      04-3965199
                    </a>
                    <span>|</span>
                    <a
                      href='tel:800-9889'
                      className='text-gray-700 dark:text-gray-300 hover:text-teal-700 dark:hover:text-teal-400 transition-colors'
                    >
                      800-9889
                    </a>
                  </div>
                </div>
                <div className='flex items-center justify-center space-x-3'>
                  <Mail className='h-5 w-5 text-teal-700 dark:text-teal-400' />
                  <a
                    href='mailto:info@x-space.ae'
                    className='text-gray-700 dark:text-gray-300 hover:text-teal-700 dark:hover:text-teal-400 transition-colors'
                  >
                    info@x-space.ae
                  </a>
                </div>
                <div className='flex items-center justify-center space-x-3'>
                  <MapPin className='h-5 w-5 text-teal-700 dark:text-teal-400' />
                  <a
                    href='https://maps.app.goo.gl/ypptzFUFJ9HRryow5'
                    target='_blank'
                    rel='noopener noreferrer'
                    className='text-gray-700 dark:text-gray-300 hover:text-teal-700 dark:hover:text-teal-400 transition-colors'
                  >
                    Dubai, UAE
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CONTACT MODAL ───────────────────────────────────────────────────── */}
      {isContactModalOpen && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
          <div className='bg-white dark:bg-gray-800 rounded-3xl p-8 max-w-md w-full max-h-[90vh] overflow-y-auto'>
            <div className='flex justify-between items-center mb-6'>
              <h3 className='text-2xl font-bold text-gray-900 dark:text-white'>
                HomeCare Enquiry
              </h3>
              <button
                onClick={() => setIsContactModalOpen(false)}
                className='text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors'
                aria-label='Close modal'
              >
                <svg className='w-6 h-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M6 18L18 6M6 6l12 12'
                  />
                </svg>
              </button>
            </div>
            <ContactFormModal
              isOpen={isContactModalOpen}
              onClose={() => setIsContactModalOpen(false)}
              title='Website Lead – X Space HomeCare Enquiry'
              subtitle='Submit your property details and maintenance requirements. Our team will be in touch within 24 hours.'
            />
          </div>
        </div>
      )}
    </main>
  );
}

export default PropertyCareClient;
